import { useState, useContext } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Input, Button, Table, Typography, Space, Row, Col, Alert, theme } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { TradesContext } from '../../contexts/tradeProcessContext';
import SelectedDateCard from './SelectedDateCard';
import { formatLabel } from '../../util/Common';
import ConfirmModal from './ConfirmModal';
import TradeProcessService from '../../services/TradeProcessService';

const { Title, Text } = Typography;

const MatchingTable = () => {
  const { token } = theme.useToken();
  const { showNotification } = useAuth();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const { matchingData, formValues, setTradeResponse, setOpenModal, currentStep, setCurrentStep } =
    useContext(TradesContext);

  const TextItem = (text, isSame) => {
    return <Text style={isSame ? { color: 'green' } : { color: 'red' }}>{text}</Text>;
  };

  const [editingRow, setEditingRow] = useState(null);

  // removed high constrast colors from table rows
  const columns = [
    {
      title: 'No.',
      dataIndex: 'index',
      key: 'index',
      align: 'center',
      render: (_, __, index) => index + 1,
    },
    {
      title: <Title level={5}>Brokerage</Title>,
      children: [
        {
          title: 'Fix.Execu.ID',
          dataIndex: 'fixExecutionIdAtBroker',
          key: 'fixExecutionIdAtBroker',
          render: (text, record) => TextItem(text, record.same),
        },
        {
          title: 'Side',
          dataIndex: 'orderSideTypeAtBroker',
          key: 'orderSideTypeAtBroker',
          render: (text, record) => TextItem(text, record.same),
        },
        {
          title: 'Security Code',
          dataIndex: 'securityCodeAtBroker',
          key: 'securityCodeAtBroker',
          render: (text, record) => {
            return editingRow === record.key ? (
              <Input
                defaultValue={text}
                onChange={e => (record.brokerage.symbolCode = e.target.value)}
                style={{ maxWidth: '80px' }}
              />
            ) : (
              TextItem(text, record.same)
            );
          },
        },
        {
          title: 'Quantity',
          dataIndex: 'quantityAtBroker',
          key: 'quantityAtBroker',
          align: 'right',
          render: (text, record) => {
            return editingRow === record.key ? (
              <Input
                defaultValue={text}
                onChange={e => (record.brokerage.executedQuantity = e.target.value)}
                style={{ maxWidth: '80px' }}
              />
            ) : (
              TextItem(Number(text).toLocaleString('en-US'), record.same)
            );
          },
        },
        {
          title: 'Amount',
          dataIndex: 'amountAtBroker',
          key: 'amountAtBroker',
          align: 'right',
          render: (text, record) => {
            return editingRow === record.key ? (
              <Input
                defaultValue={text}
                onChange={e => (record.brokerage.executedPrice = e.target.value)}
                style={{ maxWidth: '80px' }}
              />
            ) : (
              TextItem(Number(text).toLocaleString('en-US'), record.same)
            );
          },
        },
      ],
    },
    {
      title: <Title level={5}>Exchange</Title>,
      children: [
        {
          title: 'Fix.Execu.ID',
          dataIndex: 'fixExecutionIdAtExchange',
          key: 'fixExecutionIdAtExchange',
          render: (text, record) => TextItem(text, record.same),
        },
        {
          title: 'Side',
          dataIndex: 'orderSideTypeAtExchange',
          key: 'orderSideTypeAtExchange',
          render: (text, record) => TextItem(text, record.same),
        },
        {
          title: 'Security Code',
          dataIndex: 'securityCodeAtExchange',
          key: 'securityCodeAtExchange',
          render: (text, record) => TextItem(text, record.same),
        },
        {
          title: 'Quantity',
          dataIndex: 'quantityAtExchange',
          key: 'quantityAtExchange',
          align: 'right',
          render: (text, record) => TextItem(Number(text).toLocaleString('en-US'), record.same),
        },
        {
          title: 'Amount',
          dataIndex: 'amountAtExchange',
          key: 'amountAtExchange',
          align: 'right',
          render: (text, record) => TextItem(Number(text).toLocaleString('en-US'), record.same),
        },
      ],
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      render: (_, record) => (
        <>
          {editingRow === record.key ? (
            <Button
              type="primary"
              onClick={() => {
                console.log(record.key);
                // setEditingRow(null);
              }}
            >
              Done
            </Button>
          ) : (
            <Button
              type="default"
              onClick={() => {
                console.log(record.key);
                setEditingRow(record.key);
              }}
              disabled={record.same}
            >
              Fix
            </Button>
          )}
        </>
      ),
    },
  ];

  const handleOk = async () => {
    setConfirmLoading(true);

    const payload = {
      tradeWindow: formValues.tradeWindow.format('YYYYMMDD'),
      transferWindow: formValues.transferWindow.format('YYYYMMDD'),
      settlementWindow: formValues.settlementWindow.format('YYYYMMDD'),
    };

    const result = await TradeProcessService.performTradeProcess(payload);

    if (result.success) {
      setTradeResponse({
        matchingProcessId: result.data.matchingProcessId,
        tradeProcessId: result.data.tradeProcessId,
        tradeWindow: formValues.tradeWindow.format('DD-MMM-YYYY'),
        settlementWindow: formValues.settlementWindow.format('DD-MMM-YYYY'),
      });

      setConfirmLoading(false);
      setOpenModal(false);
      setCurrentStep(currentStep + 1);
    } else {
      setConfirmLoading(false);
      setOpenModal(false);
      showNotification('error', result.message);
      setCurrentStep(currentStep + 1);
    }
  };

  const handleCancel = () => {
    setOpenModal(false);
  };

  return (
    <Space
      direction="vertical"
      size={'large'}
      style={{
        width: '100%',
        marginTop: '24px',
      }}
    >
      {formValues && (
        <Row gutter={[16, 16]}>
          {Object.entries(formValues).map(([key, value]) =>
            key === 'notes' ? null : (
              <Col key={key} xs={24} sm={12} lg={8}>
                <SelectedDateCard label={formatLabel(key)} value={value} />
              </Col>
            ),
          )}
        </Row>
      )}

      <Table
        onRow={record => ({
          style: {
            backgroundColor: record.same ? `${token.colorSuccess}1A` : `${token.colorError}1A`,
          },
        })}
        columns={columns}
        dataSource={matchingData.records.map(record => ({
          ...record,
          key: uuidv4(), // Generate a unique identifier using uuidv4()
        }))}
        size={'small'}
        scroll={{ x: 'max-content' }}
        bordered
      />

      <ConfirmModal
        title={'Please double check !'}
        description={'You have selected below dates to do trading process'}
        onOk={handleOk}
        onCancel={handleCancel}
        confirmLoading={confirmLoading}
        okText="Confirm"
        cancelText="Cancel"
      />
    </Space>
  );
};

export default MatchingTable;
